/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Save, FileText, Image, X, Stethoscope, Euro, Paperclip, Home, Tag, Search } from 'lucide-react';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  nome: '', categoria: '', descrizione: '', descrizione_dettaglio: '',
  immagine_url: '', immagine_url_2: '', documento_url: '',
  durata_minuti: 30, prezzo: 0, pagamento_online: 'none', acconto_importo: 0, home_page: false,
  promozione: false, promo_scadenza: ''
};

export default function ServizioEdit() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const isNew = !serviceId || serviceId === 'nuovo';

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const [serviceActive, setServiceActive] = useState(true);
  const [categories, setCategories] = useState([]);
  // Media picker
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaSearch, setMediaSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Carica le categorie esistenti per i suggerimenti
      try {
        const listRes = await axios.get(`${API}/services?active_only=false`);
        setCategories([...new Set(listRes.data.map(s => s.categoria))].filter(Boolean).sort((a, b) => a.localeCompare(b, 'it')));
      } catch (e) { logger.warn('Categorie non caricate:', e?.message); }

      if (isNew) {
        setForm(EMPTY_FORM);
      } else {
        const res = await axios.get(`${API}/services/${serviceId}`);
        const s = res.data;
        setServiceName(s.nome);
        setServiceActive(s.active !== false);
        setForm({
          nome: s.nome, categoria: s.categoria,
          descrizione: s.descrizione || '', descrizione_dettaglio: s.descrizione_dettaglio || '',
          immagine_url: s.immagine_url || '', immagine_url_2: s.immagine_url_2 || '', documento_url: s.documento_url || '',
          durata_minuti: s.durata_minuti || 30, prezzo: s.prezzo ?? 0,
          pagamento_online: s.pagamento_online || 'none', acconto_importo: s.acconto_importo ?? 0,
          home_page: !!s.home_page,
          promozione: !!s.promozione, promo_scadenza: s.promo_scadenza || ''
        });
      }
    } catch (err) {
      logger.error('Load error:', err);
      toast.error('Servizio non trovato');
      navigate('/backoffice/servizi');
    } finally { setLoading(false); }
  }, [serviceId, isNew, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.nome.trim()) { toast.error('Il nome del servizio è obbligatorio'); return; }
    if (!form.categoria.trim()) { toast.error('La tipologia/categoria è obbligatoria'); return; }
    const data = {
      ...form,
      prezzo: parseFloat(form.prezzo) || 0,
      durata_minuti: parseInt(form.durata_minuti) || 30,
      acconto_importo: parseFloat(form.acconto_importo) || 0
    };
    if (data.promozione && data.promo_scadenza && data.promo_scadenza < new Date().toISOString().slice(0, 10)) {
      toast.error('La data di scadenza della promozione è già passata');
      return;
    }
    if (data.pagamento_online === 'deposit' && data.acconto_importo <= 0) {
      toast.error('Inserisci un importo acconto maggiore di zero');
      return;
    }
    if (data.pagamento_online === 'full' && data.prezzo <= 0) {
      toast.error('Per il saldo completo online serve un prezzo del servizio (o una tariffa medico)');
    }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/services`, { ...data, active: true }, {});
        toast.success('Servizio creato');
      } else {
        await axios.put(`${API}/services/${serviceId}`, data, {});
        toast.success('Servizio aggiornato');
      }
      navigate('/backoffice/servizi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  // ── Media picker ──
  const isImageFile = (name) => ['jpg','jpeg','png','gif','webp','svg'].includes((name||'').toLowerCase().split('.').pop());

  const openMediaPicker = async (targetField) => {
    setMediaPickerTarget(targetField);
    try {
      const res = await axios.get(`${API}/media`);
      setMediaFiles(res.data);
    } catch { setMediaFiles([]); }
    setMediaSearch(""); setMediaPickerOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded" />
        <div className="grid lg:grid-cols-2 gap-6">
          <Skeleton className="h-96 rounded-xl" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="service-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/backoffice/servizi')} data-testid="service-edit-back-btn">
            <ArrowLeft size={16} className="mr-1" /> Servizi
          </Button>
          <div>
            <PageTitle
              title={isNew ? 'Nuovo Servizio' : `Modifica — ${serviceName}`}
              info={isNew ? 'Compila la scheda del nuovo servizio: nome, categoria, durata, prezzo, pagamento online, visibilità in Home e promozione.' : 'Scheda completa del servizio: dati, prezzo, pagamento online, immagine e documento, visibilità in Home e promozione.'}
              extra={!isNew && !serviceActive ? <Badge variant="secondary">Inattivo</Badge> : null}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/backoffice/servizi')} data-testid="service-edit-cancel-btn">Annulla</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="service-edit-save-btn">
            <Save size={15} className="mr-1" /> {saving ? 'Salvataggio...' : (isNew ? 'Crea Servizio' : 'Salva Modifiche')}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {/* Colonna sinistra: Informazioni */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><Stethoscope size={18} /> Informazioni Servizio</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input value={form.nome} onChange={e => setForm(p => ({...p, nome: e.target.value}))} data-testid="service-nome-input" />
              </div>
              <div className="space-y-2">
                <Label>Tipologia / Categoria</Label>
                <Input list="service-categories" value={form.categoria} onChange={e => setForm(p => ({...p, categoria: e.target.value}))} placeholder="es. Ginecologia" data-testid="service-categoria-input" />
                <datalist id="service-categories">
                  {categories.map(c => <option key={c} value={c} />)}
                </datalist>
                {categories.length > 0 && (
                  <p className="text-xs text-muted-foreground">Tipologie esistenti: {categories.join(', ')}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Descrizione breve</Label>
                <Textarea value={form.descrizione} onChange={e => setForm(p => ({...p, descrizione: e.target.value}))} rows={2} placeholder="Riassunto breve visibile nella lista servizi" data-testid="service-descrizione-input" />
              </div>
              <div className="space-y-2">
                <Label>Descrizione dettagliata</Label>
                <Textarea value={form.descrizione_dettaglio} onChange={e => setForm(p => ({...p, descrizione_dettaglio: e.target.value}))} rows={6} placeholder="Descrizione completa visibile nella pagina di dettaglio" data-testid="service-descrizione-dettaglio-input" />
              </div>
              <div className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-[hsl(var(--secondary))]">
                <div className="space-y-0.5">
                  <Label htmlFor="service-home-switch" className="flex items-center gap-2 cursor-pointer"><Home size={14} className="text-[#238A92]" /> Mostra in Home Page</Label>
                  <p className="text-xs text-muted-foreground">Il servizio comparirà nella sezione "I Nostri Servizi" della pagina iniziale.</p>
                </div>
                <Switch id="service-home-switch" checked={!!form.home_page} onCheckedChange={v => setForm(p => ({...p, home_page: v}))} data-testid="service-home-switch" />
              </div>
              <div className="p-3 rounded-lg border bg-[hsl(var(--secondary))] space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="service-promo-switch" className="flex items-center gap-2 cursor-pointer"><Tag size={14} className="text-[#238A92]" /> Promozione</Label>
                    <p className="text-xs text-muted-foreground">Comparirà nella sezione "Promozioni" della Home Page con il prezzo di listino, prenotabile direttamente.</p>
                  </div>
                  <Switch id="service-promo-switch" checked={!!form.promozione} onCheckedChange={v => setForm(p => ({...p, promozione: v}))} data-testid="service-promo-switch" />
                </div>
                {form.promozione && (
                  <div className="space-y-2">
                    <Label htmlFor="service-promo-scadenza">Valida fino al (opzionale)</Label>
                    <Input id="service-promo-scadenza" type="date" value={form.promo_scadenza} min={new Date().toISOString().slice(0, 10)} onChange={e => setForm(p => ({...p, promo_scadenza: e.target.value}))} className="max-w-xs" data-testid="service-promo-scadenza-input" />
                    <p className="text-xs text-muted-foreground">Dopo questa data la promozione sparisce automaticamente dalla Home. Lascia vuoto per nessuna scadenza.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Colonna destra: Prezzo, pagamenti e media */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><Euro size={18} /> Durata, Prezzo e Pagamento Online</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Durata (minuti)</Label>
                  <Input type="number" min="5" step="5" value={form.durata_minuti} onChange={e => setForm(p => ({...p, durata_minuti: e.target.value}))} data-testid="service-durata-input" />
                </div>
                <div className="space-y-2">
                  <Label>Prezzo di listino (EUR)</Label>
                  <Input type="number" step="0.01" min="0" value={form.prezzo} onChange={e => setForm(p => ({...p, prezzo: e.target.value}))} data-testid="service-prezzo-input" />
                  <p className="text-xs text-muted-foreground">Usato quando il medico non ha una tariffa personalizzata.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 p-3 rounded-lg border bg-[hsl(var(--secondary))]">
                <div className="space-y-2">
                  <Label>Pagamento online</Label>
                  <Select value={form.pagamento_online} onValueChange={v => setForm(p => ({...p, pagamento_online: v}))}>
                    <SelectTrigger data-testid="service-payment-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno (si paga in sede)</SelectItem>
                      <SelectItem value="full">Saldo completo</SelectItem>
                      <SelectItem value="deposit">Acconto fisso</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Richiede pagamenti online attivi nelle Impostazioni. Il saldo usa la tariffa del medico se presente, altrimenti il prezzo del servizio.</p>
                </div>
                {form.pagamento_online === 'deposit' && (
                  <div className="space-y-2">
                    <Label>Importo acconto (EUR)</Label>
                    <Input type="number" step="0.01" min="0" value={form.acconto_importo} onChange={e => setForm(p => ({...p, acconto_importo: e.target.value}))} data-testid="service-deposit-amount-input" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><Paperclip size={18} /> Immagini e Documenti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Immagine 1 */}
              <div className="space-y-2">
                <Label>Immagine 1</Label>
                <div className="flex gap-2">
                  <Input value={form.immagine_url} onChange={e => setForm(p => ({...p, immagine_url: e.target.value}))} placeholder="URL o seleziona dai media" className="flex-1" data-testid="service-immagine1-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => openMediaPicker('immagine_url')}><Image size={14} className="mr-1" /> Media</Button>
                </div>
                {form.immagine_url && (
                  <div className="flex items-center gap-3">
                    <img src={form.immagine_url} alt="Anteprima" className="h-20 rounded-lg object-cover border" onError={e => { e.target.style.display = 'none'; }} />
                    <Button type="button" variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => setForm(p => ({...p, immagine_url: ''}))}><X size={12} className="mr-1" /> Rimuovi</Button>
                  </div>
                )}
              </div>
              {/* Immagine 2 */}
              <div className="space-y-2">
                <Label>Immagine 2 (opzionale)</Label>
                <div className="flex gap-2">
                  <Input value={form.immagine_url_2} onChange={e => setForm(p => ({...p, immagine_url_2: e.target.value}))} placeholder="URL o seleziona dai media" className="flex-1" data-testid="service-immagine2-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => openMediaPicker('immagine_url_2')}><Image size={14} className="mr-1" /> Media</Button>
                </div>
                {form.immagine_url_2 && (
                  <div className="flex items-center gap-3">
                    <img src={form.immagine_url_2} alt="Anteprima 2" className="h-20 rounded-lg object-cover border" onError={e => { e.target.style.display = 'none'; }} />
                    <Button type="button" variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => setForm(p => ({...p, immagine_url_2: ''}))}><X size={12} className="mr-1" /> Rimuovi</Button>
                  </div>
                )}
              </div>
              {/* Documento */}
              <div className="space-y-2">
                <Label>Documento scaricabile (PDF, DOC...)</Label>
                <div className="flex gap-2">
                  <Input value={form.documento_url} onChange={e => setForm(p => ({...p, documento_url: e.target.value}))} placeholder="URL o seleziona dai media" className="flex-1" data-testid="service-documento-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => openMediaPicker('documento_url')}><FileText size={14} className="mr-1" /> Media</Button>
                </div>
                {form.documento_url && (
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-muted-foreground">Documento: {form.documento_url.split('/').pop()}</p>
                    <Button type="button" variant="ghost" size="sm" className="text-red-500 h-6 px-2" onClick={() => setForm(p => ({...p, documento_url: ''}))}><X size={12} /></Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Save in fondo per comodità */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate('/backoffice/servizi')}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="service-edit-save-btn-bottom">
              <Save size={15} className="mr-1" /> {saving ? 'Salvataggio...' : (isNew ? 'Crea Servizio' : 'Salva Modifiche')}
            </Button>
          </div>
        </div>
      </div>

      {/* Media Picker Dialog */}
      <Dialog open={mediaPickerOpen} onOpenChange={setMediaPickerOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Space Grotesk'}}>Seleziona {mediaPickerTarget === 'documento_url' ? 'documento' : 'immagine'}</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={mediaSearch} onChange={e => setMediaSearch(e.target.value)} placeholder="Cerca per nome file..." className="pl-8 h-9" autoFocus data-testid="service-media-search" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            {mediaFiles
              .filter(m => mediaPickerTarget === 'documento_url' ? !isImageFile(m.original_name) : isImageFile(m.original_name))
              .filter(m => { const mq = mediaSearch.trim().toLowerCase(); return !mq || (m.original_name || '').toLowerCase().includes(mq) || (m.filename || '').toLowerCase().includes(mq); })
              .map(m => (
              <button key={m.id} onClick={() => { setForm(p => ({...p, [mediaPickerTarget]: m.url})); setMediaPickerOpen(false); toast.success('File selezionato'); }}
                className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-[#238A92] hover:shadow-md transition-all relative group">
                {isImageFile(m.original_name) ? (
                  <img src={m.url} alt={m.original_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-white">
                    <FileText size={28} className="text-red-500" />
                    <p className="text-[9px] text-muted-foreground mt-1 uppercase">{(m.original_name||'').split('.').pop()}</p>
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[9px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">{m.original_name}</div>
              </button>
            ))}
          </div>
          {mediaFiles.filter(m => mediaPickerTarget === 'documento_url' ? !isImageFile(m.original_name) : isImageFile(m.original_name)).filter(m => { const mq = mediaSearch.trim().toLowerCase(); return !mq || (m.original_name || '').toLowerCase().includes(mq) || (m.filename || '').toLowerCase().includes(mq); }).length === 0 && (
            <p className="text-center text-muted-foreground py-8">{mediaSearch.trim() ? `Nessun file contiene "${mediaSearch.trim()}"` : 'Nessun file trovato. Carica i file nella Media Library.'}</p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMediaPickerOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
